/**
 * The one-word status beside a consultation's "send to GP" button, and the tooltip that explains it.
 *
 * Two sources: the send history (what actually went to SES, by a person or automatically), and the
 * automatic letter's own state (what happened to it when nothing went). A recorded send always wins -
 * it is the fact; the automatic state only explains why there is none.
 *
 * "Not sent" and "No record" stay distinct: sends were only recorded from the day tracking shipped,
 * so an empty history on an older consultation means the answer is unknown, not that nothing went.
 */
const WHY_LEFT_FOR_ADMIN = {
    needs_confirmation: 'the surgery match needs confirming',
    alias_match: 'the surgery was matched from an earlier confirmation, so it needs checking',
    no_match: 'the patient\'s surgery was not recognised',
    no_surgery: 'the patient has no surgery on record',
    no_email: 'the matched practice has no email address',
    no_letter_type: 'no letter type was chosen in the notes',
    notes_pdf_missing: 'the notes PDF was not ready in time',
};

const COULD_NOT_CHECK = 'it could not be checked automatically';

/**
 * @param {object|null} sendHistory  GET /consultations/:id/gp-sends
 * @returns {{label: string, className: string, title: (string|undefined)}|null}
 */
export function describeGpSendStatus(sendHistory) {
    if (!sendHistory) {
        return null;
    }

    const latestSend = (sendHistory.data || [])[0] || null;

    if (latestSend) {
        const automatic = latestSend.triggeredBy === 'auto';

        // "Submitted", not "Sent": SES accepted it; a later bounce is not represented.
        if (latestSend.status === 'failed') {
            return {
                label: automatic ? 'Automatic send failed' : 'Failed',
                className: 'text-danger',
                title: automatic ? 'The automatic letter was not accepted by the mail provider. Send it by hand.' : undefined,
            };
        }

        return {
            label: automatic ? 'Submitted automatically' : 'Submitted',
            className: 'text-success',
            title: 'Accepted by the mail provider. Delivery to the surgery is not confirmed.',
        };
    }

    const autoSend = sendHistory.autoSend || null;
    const state = autoSend && autoSend.state;

    if (state === 'queued' || state === 'sending') {
        return {
            label: 'Automatic letter pending',
            className: 'text-info',
            title: 'Will be sent automatically once it is due. No need to send it by hand.',
        };
    }

    if (state === 'left_for_admin' || state === 'failed') {
        const why = state === 'failed'
            ? 'the automatic send did not go through'
            : (WHY_LEFT_FOR_ADMIN[autoSend.reason] || COULD_NOT_CHECK);
        return {
            label: 'Needs sending by hand',
            className: 'text-warning',
            title: `Not sent automatically because ${why}.`,
        };
    }

    if (state === 'skipped_no_consent') {
        return {
            label: 'Not sent - no consent',
            className: 'text-muted',
            title: 'The patient has not consented to sharing their information with their GP.',
        };
    }

    if (state === 'dry_run') {
        return {
            label: 'Would send automatically (test mode)',
            className: 'text-muted',
            title: 'Automatic letters are in dry-run mode: this one would have been sent, but nothing was.',
        };
    }

    return sendHistory.tracked
        ? { label: 'Not sent', className: 'text-muted', title: undefined }
        : {
            label: 'No record',
            className: 'text-muted',
            title: 'This consultation predates send tracking, so we cannot say whether a letter was sent.',
        };
}
